// IRealChart ⇄ Score converters — the migration seam.
//
// chartToScore is lossless: the chart's structure and chords become the form
// + harmony lane; its tick melody becomes a single-staff "Lead" part (rests
// filled in, triplet tick values regrouped into explicit tuplets, spellings
// made explicit). scoreToChart is the lossy projection back: single part,
// single voice, top notehead of any chord — exactly what a lead sheet holds.
//
// Chart melody ticks are 48 per *chart beat* (the time signature's
// denominator unit), while score durations are notated values; the bridge is
// beat = 4/denominator quarter notes, kept exact with Fraction arithmetic.

import type { ChartMelodyNote } from '../chartMelody';
import { TICKS_PER_BEAT } from '../chartMelody';
import type { IRealChart, IRealChord, IRealMeasure } from '../ireal/types';
import { parseKey } from '../notes';
import {
  addF,
  compareF,
  durationQuarters,
  equalsF,
  frac,
  fractionToNumber,
  measureQuarters,
  mulF,
  NOTE_VALUES,
  type Duration,
  type Fraction,
} from './duration';
import { midiOf, noteNameOf, spellMidi } from './pitch';
import { voiceTiming } from './timing';
import type {
  FormMeasure,
  KeySignature,
  NoteEvent,
  NoteHead,
  Part,
  PartMeasure,
  Score,
  TupletGroup,
  Voice,
  VoiceItem,
} from './types';

// ---------------------------------------------------------------------------
// Key label bridging (chart "G-" ⇄ KeySignature)
// ---------------------------------------------------------------------------

/** Parse the chart's key label ("C", "G-", "Eb") into a KeySignature. */
export function keySignatureOf(label: string | undefined): KeySignature | undefined {
  if (!label) return undefined;
  const key = parseKey(label.replace(/-$/, 'm'));
  return key ? { tonic: key.tonic, mode: key.mode } : undefined;
}

/** Format a KeySignature back to the chart's display convention — the iReal
 *  parser prettifies "G-" to "G minor" (parse.ts), so the projection must
 *  emit the same or a loaded tune's key label would drift on the first lift. */
export function keyLabelOf(sig: KeySignature | undefined): string | undefined {
  if (!sig) return undefined;
  return sig.mode === 'minor' ? `${sig.tonic} minor` : sig.tonic;
}

// ---------------------------------------------------------------------------
// Duration decomposition: exact quarters → written durations
// ---------------------------------------------------------------------------

interface DurationMatch {
  duration: Duration;
  /** True when the value is the 2/3 (triplet) scaling of `duration`. */
  triplet: boolean;
}

const STRAIGHT: { duration: Duration; quarters: Fraction }[] = NOTE_VALUES.flatMap((base) =>
  ([0, 1, 2] as const).map((dots) => {
    const duration: Duration = dots ? { base, dots } : { base };
    return { duration, quarters: durationQuarters(duration) };
  }),
);

/** Match a length to one written duration. Existing explicit 3:2 members must
 * prefer the triplet interpretation: a dotted eighth under 3:2 sounds exactly
 * like a straight eighth, but rebuilding it as straight would dissolve the
 * imported group and change its written value. */
function matchDuration(quarters: Fraction, preferTriplet = false): DurationMatch | null {
  const unscaled = mulF(quarters, frac(3, 2));
  if (preferTriplet) {
    for (const cand of STRAIGHT) {
      if (equalsF(cand.quarters, unscaled)) return { duration: cand.duration, triplet: true };
    }
  }
  for (const cand of STRAIGHT) {
    if (equalsF(cand.quarters, quarters)) return { duration: cand.duration, triplet: false };
  }
  for (const cand of STRAIGHT) {
    if (equalsF(cand.quarters, unscaled)) return { duration: cand.duration, triplet: true };
  }
  return null;
}

export class UnnotatableDurationError extends Error {
  constructor(remainder: Fraction) {
    super(`Cannot notate a remainder of ${remainder.num}/${remainder.den} quarters`);
    this.name = 'UnnotatableDurationError';
  }
}

/** Largest-first split of an arbitrary length into straight written values. */
function splitStraight(quarters: Fraction): Duration[] {
  const out: Duration[] = [];
  let rest = quarters;
  while (compareF(rest, frac(0)) > 0) {
    const fit = STRAIGHT.filter((c) => compareF(c.quarters, rest) <= 0).sort((a, b) =>
      compareF(b.quarters, a.quarters),
    )[0];
    if (!fit) {
      throw new UnnotatableDurationError(rest);
    }
    out.push(fit.duration);
    rest = addF(rest, mulF(fit.quarters, frac(-1)));
  }
  return out;
}

// ---------------------------------------------------------------------------
// chartToScore
// ---------------------------------------------------------------------------

function cloneChord(chord: IRealChord): IRealChord {
  return { ...chord, alternate: chord.alternate ? { ...chord.alternate } : chord.alternate };
}

function formMeasureOf(m: IRealMeasure, index: number, chart: IRealChart): FormMeasure {
  const out: FormMeasure = { id: m.id, harmony: m.chords.map(cloneChord) };
  if (m.timeSig) out.timeSig = [...m.timeSig];
  if (index === 0) {
    out.timeSig = out.timeSig ?? [...chart.timeSignature];
    const keySig = keySignatureOf(chart.key);
    if (keySig) out.keySig = keySig;
  }
  copyStructure(m, out);
  return out;
}

/** Copy the shared structural vocabulary between IRealMeasure and FormMeasure. */
function copyStructure(
  from: Pick<FormMeasure, StructuralField>,
  to: Pick<FormMeasure, StructuralField>,
): void {
  if (from.open !== undefined) to.open = from.open;
  if (from.pickupBeats !== undefined) to.pickupBeats = from.pickupBeats;
  if (from.close !== undefined) to.close = from.close;
  if (from.section !== undefined) to.section = from.section;
  if (from.sectionRole !== undefined) to.sectionRole = from.sectionRole;
  if (from.feel !== undefined) to.feel = { ...from.feel };
  if (from.bandCue !== undefined) to.bandCue = {
    ...from.bandCue,
    ...(from.bandCue.channels ? { channels: [...from.bandCue.channels] } : {}),
    ...(from.bandCue.duration ? { duration: { ...from.bandCue.duration } } : {}),
  };
  if (from.ending !== undefined) to.ending = from.ending;
  if (from.barRepeat !== undefined) to.barRepeat = from.barRepeat;
  if (from.coda !== undefined) to.coda = from.coda;
  if (from.segno !== undefined) to.segno = from.segno;
  if (from.fermata !== undefined) to.fermata = from.fermata;
  if (from.endMarker !== undefined) to.endMarker = from.endMarker;
  if (from.staffText !== undefined) to.staffText = from.staffText;
  if (from.staffTextAbove !== undefined) to.staffTextAbove = from.staffTextAbove;
  if (from.directive !== undefined) to.directive = from.directive;
  if (from.cell !== undefined) to.cell = from.cell;
  if (from.cells !== undefined) to.cells = from.cells;
  if (from.spacer !== undefined) to.spacer = from.spacer;
}

type StructuralField =
  | 'open'
  | 'pickupBeats'
  | 'close'
  | 'section'
  | 'sectionRole'
  | 'feel'
  | 'bandCue'
  | 'ending'
  | 'barRepeat'
  | 'coda'
  | 'segno'
  | 'fermata'
  | 'endMarker'
  | 'staffText'
  | 'staffTextAbove'
  | 'directive'
  | 'cell'
  | 'cells'
  | 'spacer';

/**
 * A contiguous span of written time awaiting conversion to voice items:
 * either a (possibly multi-notehead) note or, when `noteheads` is absent, a
 * rest. The shared currency between the chart lift (tick melody) and the
 * native voice editor (voiceEdit.ts) — both build slots, one builder writes
 * items, so triplet grouping and tied splitting can never diverge.
 */
export interface WrittenSlot {
  quarters: Fraction;
  /** Stable id: the source event's id, or a derived id for synthesized rests. */
  idBase: string;
  /** Existing explicit 3:2 group boundary retained by native score edits. */
  tupletGroup?: string;
  /** Sounding content with per-notehead ties; absent = rest. */
  noteheads?: NoteHead[];
  /** Optional authored metadata retained when an editor rebuilds this slot. */
  source?: NoteEvent;
  /** Authored metadata for a newly minted event that has no source event. */
  metadata?: Omit<NoteEvent, 'id' | 'kind' | 'duration' | 'notes'>;
}

/**
 * Convert contiguous slots into voice items: exact written values pass
 * through, triplet-scaled values group into explicit 3:2 tuplets, and
 * unrepresentable lengths split into tied pieces (piece n's id derives as
 * `${idBase}~n`, so splits are stable across lifts). DETERMINISTIC — this
 * runs inside the repeatedly-recomputed projection and the persistence
 * migration; nothing here may mint a random id.
 */
export function slotsToItems(slots: WrittenSlot[]): VoiceItem[] {
  const items: VoiceItem[] = [];
  let tripletRun: NoteEvent[] = [];
  let tripletGroup: string | undefined;
  const flushTriplets = () => {
    if (tripletRun.length === 0) return;
    const group: TupletGroup = {
      id: tripletGroup ?? `tup:${tripletRun[0].id}`,
      kind: 'tuplet',
      actual: 3,
      normal: 2,
      items: tripletRun,
    };
    items.push(group);
    tripletRun = [];
    tripletGroup = undefined;
  };

  for (const slot of slots) {
    const match = matchDuration(slot.quarters, slot.tupletGroup !== undefined);
    if (match) {
      const event = slotEvent(slot, match.duration);
      if (match.triplet) {
        // Adjacent imported triplet groups are distinct notation even when
        // every member has the same sounding duration. Do not fuse their
        // brackets merely because the editor flattened them to timed slots.
        if (tripletRun.length > 0 && slot.tupletGroup !== tripletGroup) flushTriplets();
        if (tripletRun.length === 0) tripletGroup = slot.tupletGroup;
        tripletRun.push(event);
      } else {
        flushTriplets();
        items.push(event);
      }
      continue;
    }
    // No single written value: split into tied pieces (rests just repeat).
    flushTriplets();
    const pieces = splitStraight(slot.quarters);
    pieces.forEach((duration, i) => {
      const event = slotEvent(slot, duration, i > 0 ? `${slot.idBase}~${i}` : undefined);
      if (slot.noteheads) {
        // Chain-tie the pieces; each notehead keeps its own incoming/outgoing.
        event.notes = event.notes.map((nh) => {
          const sourceStop = nh.tie === 'stop' || nh.tie === 'both';
          const sourceStart = nh.tie === 'start' || nh.tie === 'both';
          const stop = i > 0 || sourceStop;
          const start = i < pieces.length - 1 || sourceStart;
          const tie =
            stop && start
              ? ('both' as const)
              : stop
                ? ('stop' as const)
                : start
                  ? ('start' as const)
                  : undefined;
          const out: NoteHead = { pitch: nh.pitch };
          if (tie) out.tie = tie;
          return out;
        });
      }
      items.push(event);
    });
  }
  flushTriplets();
  return items;
}

function slotEvent(slot: WrittenSlot, duration: Duration, idOverride?: string): NoteEvent {
  const metadata = slot.source ?? slot.metadata;
  // A split event's metadata belongs on its first written piece only; copying
  // lyrics, dynamics or ornaments to every tied continuation duplicates ink.
  const source = !idOverride ? metadata : undefined;
  // Cue size and cross-staff placement describe every visible continuation,
  // unlike one-shot marks above. Carry those two fields through tied splits.
  const continuation = idOverride
    ? {
        ...(metadata?.cue !== undefined ? { cue: metadata.cue } : {}),
        ...(metadata?.staff !== undefined ? { staff: metadata.staff } : {}),
        ...(metadata?.slash ? { slash: metadata.slash } : {}),
      }
    : {};
  if (!slot.noteheads) {
    return {
      ...continuation,
      ...(source ?? {}),
      id: idOverride ?? slot.idBase,
      kind: 'rest',
      duration,
      notes: [],
    };
  }
  return {
    ...continuation,
    ...(source ?? {}),
    id: idOverride ?? slot.idBase,
    kind: 'note',
    duration,
    notes: slot.noteheads.map((nh) => ({ ...nh })),
  };
}

/**
 * Convert one measure's tick melody into voice items (rests filled in — a
 * written bar accounts for the whole of its time). Synthesized rest ids
 * derive from measure id + tick position.
 */
function melodyToItems(
  notes: ChartMelodyNote[],
  timeSig: [number, number],
  key: KeySignature | undefined,
  measureId: string,
): VoiceItem[] {
  const beatQuarters = frac(4, timeSig[1]);
  const toQuarters = (ticks: number) => mulF(frac(ticks, TICKS_PER_BEAT), beatQuarters);
  const capacity = measureQuarters(timeSig);
  const keyContext = key ? { tonic: key.tonic, mode: key.mode } : null;

  const noteheadsOf = (n: ChartMelodyNote): NoteHead[] => {
    let pitch;
    try {
      pitch = spellMidi(n.midi, n.spelledAs ? { name: n.spelledAs } : { key: keyContext });
    } catch {
      // A spelledAs inconsistent with the MIDI number (bad persisted data)
      // must not sink the conversion — fall back to key-based spelling.
      pitch = spellMidi(n.midi, { key: keyContext });
    }
    return [{ pitch, ...(n.tie ? { tie: n.tie } : {}) }];
  };

  const slots: WrittenSlot[] = [];
  let cursorTicks = 0;
  for (const n of [...notes].sort((a, b) => a.startTicks - b.startTicks)) {
    if (n.startTicks > cursorTicks) {
      slots.push({
        quarters: toQuarters(n.startTicks - cursorTicks),
        idBase: `r:${measureId}:${cursorTicks}`,
      });
    }
    slots.push({
      quarters: toQuarters(n.durationTicks),
      idBase: n.id,
      ...(n.rest ? {} : { noteheads: noteheadsOf(n) }),
    });
    cursorTicks = n.startTicks + n.durationTicks;
  }
  const tail = addF(capacity, mulF(toQuarters(cursorTicks), frac(-1)));
  if (compareF(tail, frac(0)) > 0) {
    slots.push({ quarters: tail, idBase: `r:${measureId}:${cursorTicks}` });
  }
  return slotsToItems(slots);
}

/**
 * Lift a chart into the score model. Lossless: structure + chords become the
 * form; the melody (when present) becomes a one-staff "Lead" part.
 */
export function chartToScore(chart: IRealChart): Score {
  const form = chart.measures.map((m, i) => formMeasureOf(m, i, chart));
  const keySig = form[0]?.keySig;

  const hasMelody = chart.measures.some((m) => (m.melody?.length ?? 0) > 0);
  const parts: Part[] = [];
  if (hasMelody) {
    let timeSig: [number, number] = chart.timeSignature;
    const measures: PartMeasure[] = chart.measures.map((m) => {
      timeSig = m.timeSig ?? timeSig;
      const voices: Voice[] = [];
      if ((m.melody?.length ?? 0) > 0) {
        // The lift must be TOTAL: this runs as the persistence migration, so
        // corrupt stored ticks (an un-notatable remainder makes splitStraight
        // throw) drop THAT measure's melody — never the chart.
        try {
          voices.push({
            id: `v:${m.id}:0`,
            items: melodyToItems(m.melody!, timeSig, keySig, m.id),
          });
        } catch {
          // measure keeps its chords; melody for this bar is unrecoverable
        }
      }
      return { id: `pm:${m.id}`, voices };
    });
    parts.push({
      id: 'part:lead',
      name: 'Lead',
      instrument: 'lead',
      role: 'lead',
      staves: [{ id: 'staff:lead:0', clef: 'treble8vb', staffType: 'standard' }],
      measures,
    });
  }

  return {
    ...(chart.id !== undefined ? { id: chart.id } : {}),
    title: chart.title,
    ...(chart.composer !== undefined ? { composer: chart.composer } : {}),
    ...(chart.style !== undefined ? { style: chart.style } : {}),
    ...(chart.tempo !== undefined ? { tempo: chart.tempo } : {}),
    ...(chart.repeats !== undefined ? { repeats: chart.repeats } : {}),
    form,
    parts,
  };
}

// ---------------------------------------------------------------------------
// scoreToChart
// ---------------------------------------------------------------------------

/**
 * Project a score back onto the lead-sheet chart. Lossy by design: only the
 * lead-role part's first voice survives (falling back to the first legacy part
 * without role metadata), and a chord event contributes its top notehead
 * (chart melody is monophonic). Structure and harmony are exact.
 */
export function scoreToChart(score: Score): IRealChart {
  const lead =
    score.parts.find((part) => part.role === 'lead') ??
    score.parts.find((part) => part.role === undefined);
  let timeSig: [number, number] = score.form[0]?.timeSig ?? [4, 4];

  const measures: IRealMeasure[] = score.form.map((fm, i) => {
    timeSig = fm.timeSig ?? timeSig;
    const out: IRealMeasure = { id: fm.id, chords: fm.harmony.map(cloneChord) };
    if (fm.timeSig) out.timeSig = [...fm.timeSig];
    copyStructure(fm, out);

    // Playback treats the source as THE PART, exactly as written (user ruling
    // Aug 2026, realbook's All Blues): every voice on every staff, and every
    // notehead of a stack — a grand-staff piano import plays its dyads and
    // its written bassline, not just the top line. Simultaneous entries
    // simply coexist in the tick list; the player schedules each.
    const voices = lead?.measures[i]?.voices ?? [];
    if (voices.length > 0) {
      const ticksPerQuarter = frac(TICKS_PER_BEAT * timeSig[1], 4);
      const melody: ChartMelodyNote[] = [];
      for (const voice of voices) {
        for (const timed of voiceTiming(voice)) {
          if (timed.event.kind !== 'note' || timed.event.grace) continue;
          for (const head of timed.event.notes) {
            melody.push({
              id: timed.event.id,
              midi: midiOf(head.pitch),
              spelledAs: noteNameOf(head.pitch),
              startTicks: Math.round(fractionToNumber(mulF(timed.start, ticksPerQuarter))),
              durationTicks: Math.round(fractionToNumber(mulF(timed.duration, ticksPerQuarter))),
              ...(head.tie ? { tie: head.tie } : {}),
            });
          }
        }
      }
      melody.sort((a, b) => a.startTicks - b.startTicks || a.midi - b.midi);
      if (melody.length > 0) out.melody = melody;
    }
    return out;
  });

  return {
    ...(score.id !== undefined ? { id: score.id } : {}),
    title: score.title,
    ...(score.composer !== undefined ? { composer: score.composer } : {}),
    ...(score.style !== undefined ? { style: score.style } : {}),
    ...(score.tempo !== undefined ? { tempo: score.tempo } : {}),
    ...(score.repeats !== undefined ? { repeats: score.repeats } : {}),
    key: keyLabelOf(score.form[0]?.keySig),
    timeSignature: score.form[0]?.timeSig ?? [4, 4],
    measures,
  };
}
